import { useCallback, useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Form } from '../../components/ui/form';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Phone,
  Mail,
  Download,
  RefreshCw,
  CalendarDays,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { bulkUpdateLeads, createLead, deleteLead, exportLeads, getAgents, getEmployees, getLeads } from '../../lib/api';
import { toast } from 'sonner';
import {
  DeleteModal,
  EditLeadModal,
  ScheduleFollowUpModal,
  UpdateLeadStatusModal,
} from '../../components/ui/ActionModals';
import { FollowUpLeadsPage } from '../../components/common/LeadFollowUpViews';
import ExportDialog from '../../components/common/ExportDialog';
import {
  LEAD_DEFAULT_VALUES,
  LEAD_LOAN_TYPE_OPTIONS,
  LEAD_PRIORITY_OPTIONS,
  LEAD_SOURCE_OPTIONS,
  buildLeadCreatePayload,
  getEntityErrorMessage,
  leadCreateSchema,
  mapEntityApiErrors,
} from '../../lib/entityFormValidation';

const statusColors = {
  Contacted: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  Converted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  Pending: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  Rejected: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
};

const priorityColors = {
  High: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  Medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  Low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const loanTypeDisplay = (value) => {
  const map = {
    home: 'Home Loan',
    personal: 'Personal Loan',
    business: 'Business Loan',
    car: 'Car Loan',
    education: 'Education Loan',
    other: 'Other',
  };
  return map[value] || value;
};

const apiStatusToDisplay = (value) => {
  if (value === 'follow_up') return 'Contacted';
  if (value === 'converted') return 'Converted';
  if (value === 'rejected') return 'Rejected';
  return 'Pending';
};

const mapLead = (l) => ({
  id: l.id,
  name: l.customer_name,
  phone: l.phone,
  email: l.email || '',
  loanType: l.loan_type,
  loanTypeLabel: loanTypeDisplay(l.loan_type),
  amount: Number(l.loan_amount) || 0,
  status: apiStatusToDisplay(l.status),
  priority: l.priority || 'Medium',
  source: l.source || 'admin',
  assignedTo: l.assigned_to_name || l.assigned_to || '—',
  createdBy: l.created_by_name || 'Unknown',
  createdByRole: l.created_by_role || '',
  followUpDate: l.follow_up_date || null,
});

const leadStatusOptions = [
  { value: 'new', label: 'New' },
  { value: 'pending', label: 'Pending' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'rejected', label: 'Rejected' },
];

const leadPriorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const buildPaginationItems = (page, totalPages) => {
  const pages = [];
  for (let current = 1; current <= totalPages; current += 1) {
    if (current === 1 || current === totalPages || Math.abs(current - page) <= 1) {
      pages.push(current);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return pages;
};

function TablePagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  const items = buildPaginationItems(page, totalPages);
  return (
    <Pagination className="justify-end">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page > 1) onPageChange(page - 1);
            }}
            className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
        {items.map((item, index) => (
          <PaginationItem key={`${item}-${index}`}>
            {item === '...' ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                href="#"
                isActive={item === page}
                onClick={(event) => {
                  event.preventDefault();
                  onPageChange(item);
                }}
              >
                {item}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}
        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page < totalPages) onPageChange(page + 1);
            }}
            className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

const displayStatusToApi = (value) => {
  if (value === 'Contacted') return 'contacted';
  if (value === 'Converted') return 'converted';
  if (value === 'Pending') return 'pending';
  if (value === 'Rejected') return 'rejected';
  return undefined;
};

const LeadsTable = ({
  filter = 'all',
  title = 'Leads Management',
  description = 'Manage and track all your leads in one place',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(filter || 'all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [modal, setModal] = useState(null); // 'view'|'edit'|'status'|'followup'|'delete'
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
  });
  const { token, user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkPriority, setBulkPriority] = useState('');
  const [bulkAssignAgentId, setBulkAssignAgentId] = useState('');
  const [bulkAssignEmployeeId, setBulkAssignEmployeeId] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [allAgents, setAllAgents] = useState([]); // all agents from /agents — filtered client-side
  const addLeadForm = useForm({
    resolver: zodResolver(leadCreateSchema),
    defaultValues: LEAD_DEFAULT_VALUES,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const addForm = addLeadForm.watch();
  const setAddForm = (updater) => {
    const currentValues = addLeadForm.getValues();
    const nextValues = typeof updater === 'function' ? updater(currentValues) : updater;
    Object.entries(nextValues).forEach(([key, value]) => {
      addLeadForm.setValue(key, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  };
  const navigate = useNavigate();
  const location = useLocation();
  const canAssign = user?.role === 'super_admin' || user?.role === 'wl_admin';
  const assignableEmployees = useMemo(() => {
    if (user?.role === 'super_admin') {
      return employees.filter((e) => !e.white_label_id);
    }
    return employees;
  }, [employees, user?.role]);

  useEffect(() => {
    const subscription = addLeadForm.watch(() => {
      if (submitError) {
        setSubmitError('');
      }
    });
    return () => subscription.unsubscribe();
  }, [addLeadForm, submitError]);

  // Client-side filtered agents — no extra API calls needed
  const assignableAgents = useMemo(() => {
    if (user?.role === 'agent') return [];
    if (allAgents.length === 0) return [];
    if (user?.role === 'super_admin') {
      if (bulkAssignEmployeeId === 'Admin') {
        // "Admin Agents" selected → show agents created by Admin (created_by_id is null)
        return allAgents.filter((a) => a.created_by_id === null || a.created_by_id === undefined);
      }
      if (bulkAssignEmployeeId) {
        // show this employee's agents
        return allAgents.filter((a) => a.created_by_id === bulkAssignEmployeeId);
      }
      // default: show only agents created by super_admin themselves
      return allAgents.filter((a) => a.created_by_id === user?.id);
    }
    // employee / wl_admin: only agents they created
    return allAgents.filter((a) => a.created_by_id === user?.id);
  }, [allAgents, bulkAssignEmployeeId, user?.id, user?.role]);

  const openModal = (lead, type) => {
    setSelectedLead(lead);
    setModal(type);
  };
  const closeModal = () => {
    setModal(null);
    setSelectedLead(null);
  };

  const roleBasePath = useMemo(() => {
    const [, firstSegment] = location.pathname.split('/');
    return firstSegment ? `/${firstSegment}` : '';
  }, [location.pathname]);

  const openLeadDetails = (leadId) => navigate(`${roleBasePath}/leads/${leadId}`);

  const fetchLeads = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const statusParam = statusFilter !== 'all' ? displayStatusToApi(statusFilter) : displayStatusToApi(filter);
      const resp = await getLeads(token, {
        status: statusParam,
        q: searchTerm.trim() || undefined,
        page,
        pageSize: 10,
      });
      const items = resp?.data?.items ?? [];
      setRows(items.map(mapLead));
      const meta = resp?.data?.pagination || {};
      setPagination({
        page: Number(meta.page || page),
        pageSize: Number(meta.page_size || 10),
        total: Number(meta.total || items.length || 0),
        totalPages: Number(meta.total_pages || 1),
      });
    } catch (err) {
      setRows([]);
      setPagination({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
      });
      toast.error(err?.message || 'Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, [token, filter, page, searchTerm, statusFilter]);

  const fetchEmployees = useCallback(async () => {
    if (!token || !canAssign) return;
    try {
      const resp = await getEmployees(token);
      const items = resp?.data?.items ?? [];
      setEmployees(items);
    } catch {
      setEmployees([]);
    }
  }, [token, canAssign]);

  const fetchAllAgents = useCallback(async () => {
    if (!token || user?.role === 'agent') return;
    try {
      const resp = await getAgents(token);
      const items = resp?.data?.items ?? [];
      setAllAgents(items);
    } catch {
      setAllAgents([]);
    }
  }, [token, user?.role]);

  useEffect(() => {
    fetchLeads();
    fetchEmployees();
    fetchAllAgents();
  }, [fetchLeads, fetchEmployees, fetchAllAgents]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, filter]);

  useEffect(() => {
    setSelectedLeadIds((prev) => prev.filter((id) => rows.some((row) => row.id === id)));
  }, [rows]);

  useEffect(() => {
    if (selectedLeadIds.length === 0) {
      setBulkStatus('');
      setBulkPriority('');
      setBulkAssignAgentId('');
      setBulkAssignEmployeeId('');
    }
  }, [selectedLeadIds.length]);

  const clearSelection = () => {
    setSelectedLeadIds([]);
    setBulkStatus('');
    setBulkPriority('');
    setBulkAssignAgentId('');
    setBulkAssignEmployeeId('');
  };

  // Employee change just updates state — useMemo recomputes agents automatically
  const handleBulkEmployeeChange = (empId) => {
    setBulkAssignAgentId(''); // reset agent selection
    setBulkAssignEmployeeId(empId === '_none' ? '' : empId);
  };

  const performBulkLeadUpdate = async (payload) => {
    if (!token || selectedLeadIds.length === 0) return;
    setBulkSaving(true);
    try {
      const resp = await bulkUpdateLeads(token, {
        lead_ids: selectedLeadIds,
        ...payload,
      });
      const count = resp?.updated_count ?? 0;
      toast.success(`${count} leads updated successfully`);
      clearSelection();
      await fetchLeads();
    } catch (err) {
      toast.error(err?.message || 'Failed to update leads');
    } finally {
      setBulkSaving(false);
    }
  };

  const applyBulkLeadUpdate = async () => {
    const payload = {};
    if (bulkStatus) payload.status = bulkStatus;
    if (bulkPriority) payload.priority = bulkPriority;
    if (bulkAssignAgentId) {
      payload.assigned_to = bulkAssignAgentId;
    } else if (bulkAssignEmployeeId && bulkAssignEmployeeId !== 'Admin') {
      payload.assigned_to = bulkAssignEmployeeId;
    }
    if (bulkAssignEmployeeId) payload.employee_id = bulkAssignEmployeeId;
    if (Object.keys(payload).length === 0) return;
    await performBulkLeadUpdate(payload);
  };

  const toggleLeadSelection = (leadId, checked) => {
    setSelectedLeadIds((prev) => {
      const exists = prev.includes(leadId);
      if (checked) {
        return exists ? prev : [...prev, leadId];
      }
      return exists ? prev.filter((id) => id !== leadId) : prev;
    });
  };

  const filteredLeads = rows;

  const allRowsSelected = filteredLeads.length > 0 && filteredLeads.every((lead) => selectedLeadIds.includes(lead.id));

  const handleSelectAllChange = (checked) => {
    if (checked) {
      setSelectedLeadIds(filteredLeads.map((lead) => lead.id));
      return;
    }
    clearSelection();
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);

  const handleAddLead = async (values) => {
    const payload = buildLeadCreatePayload(values);
    setSubmitError('');
    setSubmitting(true);
    try {
      await createLead(token, payload);
      toast.success('Lead added successfully.');
      setShowAddDialog(false);
      addLeadForm.reset(LEAD_DEFAULT_VALUES);
      await fetchLeads();
    } catch (err) {
      const fieldErrors = mapEntityApiErrors(err);
      Object.entries(fieldErrors).forEach(([fieldName, message]) => {
        addLeadForm.setError(fieldName, { type: 'server', message });
      });
      const firstField = Object.keys(fieldErrors)[0];
      if (firstField) {
        addLeadForm.setFocus(firstField);
      }
      const message = getEntityErrorMessage(err, 'Failed to add lead.');
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async ({ format, startDate, endDate }) => {
    if (!token) return;
    setExporting(true);
    try {
      const statusParam = statusFilter !== 'all' ? displayStatusToApi(statusFilter) : displayStatusToApi(filter);
      await exportLeads(token, {
        format,
        status: statusParam,
        q: searchTerm.trim() || undefined,
        startDate,
        endDate,
      });
      toast.success(`Lead export downloaded (${format.toUpperCase()})`);
      setShowExportDialog(false);
    } catch (err) {
      toast.error(err?.message || 'Failed to export leads');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="leads-management">
      <EditLeadModal lead={selectedLead} open={modal === 'edit'} onClose={() => { closeModal(); fetchLeads(); }} token={token} />
      <UpdateLeadStatusModal lead={selectedLead} open={modal === 'status'} onClose={() => { closeModal(); fetchLeads(); }} token={token} />
      <ScheduleFollowUpModal lead={selectedLead} open={modal === 'followup'} onClose={closeModal} token={token} onSaved={fetchLeads} />
      <DeleteModal
        open={modal === 'delete'}
        onClose={closeModal}
        title={selectedLead?.name}
        description={selectedLead ? `Are you sure you want to delete lead "${selectedLead.name}"?` : ''}
        onConfirm={async () => {
          if (!selectedLead?.id) return;
          await deleteLead(token, selectedLead.id);
          await fetchLeads();
        }}
      />
      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        title="Export Leads"
        description="Select format and date range before exporting lead data."
        onSubmit={handleExport}
        loading={exporting}
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">{title}</h1>
          <p className="text-muted-foreground mt-1">{description}</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (open) {
            addLeadForm.reset(LEAD_DEFAULT_VALUES);
            setSubmitError('');
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-new-lead-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add New Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add New Lead</DialogTitle>
              <DialogDescription>Enter the lead details below to create a new lead.</DialogDescription>
            </DialogHeader>
            <Form {...addLeadForm}>
              <form onSubmit={addLeadForm.handleSubmit(handleAddLead)} className="space-y-4" noValidate>
                {submitError ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{submitError}</AlertDescription>
                  </Alert>
                ) : null}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" name="name" value={addForm.name} onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))} required />
                  {addLeadForm.formState.errors.name ? <p className="text-[0.8rem] font-medium text-destructive">{addLeadForm.formState.errors.name.message}</p> : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Contact</Label>
                  <Input id="phone" name="phone" value={addForm.phone} onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))} required />
                  {addLeadForm.formState.errors.phone ? <p className="text-[0.8rem] font-medium text-destructive">{addLeadForm.formState.errors.phone.message}</p> : null}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" name="email" type="email" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} />
                {addLeadForm.formState.errors.email ? <p className="text-[0.8rem] font-medium text-destructive">{addLeadForm.formState.errors.email.message}</p> : null}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Loan Type</Label>
                  <Select value={addForm.loanType} onValueChange={(val) => setAddForm((f) => ({ ...f, loanType: val }))}>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {LEAD_LOAN_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {addLeadForm.formState.errors.loanType ? <p className="text-[0.8rem] font-medium text-destructive">{addLeadForm.formState.errors.loanType.message}</p> : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount</Label>
                  <Input id="amount" name="amount" type="number" value={addForm.amount} onChange={(e) => setAddForm((f) => ({ ...f, amount: e.target.value }))} />
                  {addLeadForm.formState.errors.amount ? <p className="text-[0.8rem] font-medium text-destructive">{addLeadForm.formState.errors.amount.message}</p> : null}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select value={addForm.source} onValueChange={(val) => setAddForm((f) => ({ ...f, source: val }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LEAD_SOURCE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {addLeadForm.formState.errors.source ? <p className="text-[0.8rem] font-medium text-destructive">{addLeadForm.formState.errors.source.message}</p> : null}
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={addForm.priority} onValueChange={(val) => setAddForm((f) => ({ ...f, priority: val }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LEAD_PRIORITY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {addLeadForm.formState.errors.priority ? <p className="text-[0.8rem] font-medium text-destructive">{addLeadForm.formState.errors.priority.message}</p> : null}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Lead'}
                </Button>
              </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border border-border">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by Lead ID, name, contact, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-leads-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="status-filter-select">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Contacted">Contacted</SelectItem>
                <SelectItem value="Converted">Converted</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" data-testid="export-leads-btn" onClick={() => setShowExportDialog(true)}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </CardContent>
      </Card>

      {selectedLeadIds.length > 0 && (
        <Card className="border-2 border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-950/10 shadow-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <CardContent className="p-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex">
                <div className="h-8 w-8 rounded-full border-2 border-background bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                  <Plus className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">
                  {selectedLeadIds.length} Leads Selected
                </p>
                <p className="text-[11px] text-muted-foreground leading-none">
                  Batch update status, priority or assignment
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Select value={bulkStatus || undefined} onValueChange={(v) => setBulkStatus(v)}>
                  <SelectTrigger className="w-[150px] h-9 bg-background border-emerald-500/20" disabled={bulkSaving}>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {leadStatusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <div className={`h-2 w-2 rounded-full ${statusColors[apiStatusToDisplay(option.value)]?.split(' ')[0] || 'bg-slate-400'}`} />
                          {option.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={bulkPriority || undefined} onValueChange={(v) => setBulkPriority(v)}>
                  <SelectTrigger className="w-[130px] h-9 bg-background border-emerald-500/20" disabled={bulkSaving}>
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {leadPriorityOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <div className={`h-2 w-2 rounded-full ${priorityColors[option.label]?.split(' ')[0] || 'bg-slate-400'}`} />
                          {option.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* super_admin: Employee filter */}
                {user?.role === 'super_admin' && assignableEmployees.length > 0 && (
                  <Select value={bulkAssignEmployeeId || '_none'} onValueChange={handleBulkEmployeeChange}>
                    <SelectTrigger className="w-[155px] h-9 bg-background border-emerald-500/20" disabled={bulkSaving}>
                      <SelectValue placeholder="By Employee" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">All (my agents)</SelectItem>
                         {assignableEmployees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Agent dropdown — shows super_admin's agents by default, employee's after selection */}

                <Select value={bulkAssignAgentId || '_none'} onValueChange={(v) => setBulkAssignAgentId(v === '_none' ? '' : v)}>
                  <SelectTrigger className="w-[165px] h-9 bg-background border-emerald-500/20" disabled={bulkSaving}>
                    <SelectValue placeholder="Assign Agent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No agent</SelectItem>
                    {assignableAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />
                          {agent.full_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

              </div>

              <div className="flex items-center gap-2">
                <Button
                  onClick={applyBulkLeadUpdate}
                  disabled={bulkSaving || (!bulkStatus && !bulkPriority && !bulkAssignAgentId)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-5 font-semibold"
                >
                  {bulkSaving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Apply Changes'
                  )}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  disabled={bulkSaving}
                  className="text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 h-9"
                >
                  Close
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allRowsSelected}
                      onCheckedChange={(checked) => handleSelectAllChange(Boolean(checked))}
                      aria-label="Select all leads"
                    />
                  </TableHead>
                  <TableHead>Lead ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Loan Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Assigned To</TableHead>
                  {(user?.role === 'super_admin' || user?.role === 'employee' || user?.role === 'wl_admin') && (
                    <TableHead>Created By</TableHead>
                  )}
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.map((lead) => (
                  <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                    <TableCell className="w-12">
                      <Checkbox
                        checked={selectedLeadIds.includes(lead.id)}
                        onCheckedChange={(checked) => toggleLeadSelection(lead.id, Boolean(checked))}
                        aria-label={`Select lead ${lead.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{lead.id}</TableCell>
                    <TableCell className="font-medium">
                      <button
                        type="button"
                        className="text-left hover:text-emerald-700 hover:underline"
                        onClick={() => openLeadDetails(lead.id)}
                      >
                        {lead.name}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="flex items-center gap-1 text-sm"><Phone className="h-3 w-3 text-muted-foreground" />{lead.phone}</span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" />{lead.email || '—'}</span>
                      </div>
                    </TableCell>
                    <TableCell>{lead.loanTypeLabel}</TableCell>
                    <TableCell className="font-medium">{formatCurrency(lead.amount)}</TableCell>
                    <TableCell><Badge className={statusColors[lead.status]}>{lead.status}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className={priorityColors[lead.priority]}>{lead.priority}</Badge></TableCell>
                    <TableCell>{lead.source}</TableCell>
                    <TableCell>{lead.assignedTo}</TableCell>
                    {(user?.role === 'super_admin' || user?.role === 'employee' || user?.role === 'wl_admin') && (
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{lead.createdBy}</span>
                          <span className="text-xs text-muted-foreground">{lead.createdByRole || '—'}</span>
                        </div>
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`lead-actions-${lead.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onClick={() => openLeadDetails(lead.id)} className="gap-2 cursor-pointer">
                            <Eye className="h-4 w-4 text-blue-500" /><span>View</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openModal(lead, 'edit')} className="gap-2 cursor-pointer">
                            <Edit className="h-4 w-4 text-amber-500" /><span>Edit</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openModal(lead, 'status')} className="gap-2 cursor-pointer">
                            <RefreshCw className="h-4 w-4 text-purple-500" /><span>Update Status</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openModal(lead, 'followup')} className="gap-2 cursor-pointer">
                            <CalendarDays className="h-4 w-4 text-teal-500" /><span>Schedule Follow-Up</span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openModal(lead, 'delete')} className="gap-2 cursor-pointer text-red-600 focus:text-red-600">
                            <Trash2 className="h-4 w-4" /><span>Delete</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {(loading || filteredLeads.length === 0) && (
            <div className="text-center py-10 text-muted-foreground">
              {loading ? 'Loading leads...' : 'No leads found matching your criteria.'}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Showing page {pagination.page} of {pagination.totalPages} · {pagination.total} leads
        </p>
        <TablePagination page={page} totalPages={pagination.totalPages} onPageChange={setPage} />
      </div>
    </div>
  );
};

export const AllLeads = () => <LeadsTable filter="all" />;

export const FollowUpLeads = () => (
  <FollowUpLeadsPage />
);

export const ConvertedLeads = () => (
  <LeadsTable
    filter="Converted"
    title="Converted Leads"
    description="Successfully converted leads"
  />
);

export const PendingLeads = () => (
  <LeadsTable
    filter="Pending"
    title="Pending Leads"
    description="Leads pending contact or next action"
  />
);

export const RejectedLeads = () => (
  <LeadsTable
    filter="Rejected"
    title="Rejected Leads"
    description="Rejected lead applications"
  />
);

export default AllLeads;
